import { useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  startUpdate,
  setUpdatePhase,
  clearUpdate,
  selectUpdatePhase,
} from "../redux/slices/firmwareUpdateSlice";
import { useUpdateDeviceMutation } from "../redux/slices/djangoApi";

const TIMEOUT_MS = 300000; // 5 minutes — firmware downloads can be slow

/**
 * Hook for managing firmware update state for a single device.
 *
 * Phase transitions driven by device_status messages:
 * - downloading: firmware download in progress
 * - installing: OTA update being written to flash
 * - rebooting: device rebooting with new firmware
 * - complete: device back online with new firmware version
 *
 * @param {string} serialNumber
 * @param {object} device - current device data
 * @returns {{ phase, isUpdating, triggerUpdate }}
 */
const useFirmwareUpdate = (serialNumber, device) => {
  const dispatch = useDispatch();
  const phase = useSelector(selectUpdatePhase(serialNumber));
  const timerRef = useRef(null);
  const [updateDevice] = useUpdateDeviceMutation();

  const isUpdating = !!phase && phase !== "complete";

  // Auto-detect update in progress from persisted device_status on page load
  useEffect(() => {
    if (phase) return;
    const ds = device?.device_status?.toLowerCase() || "";
    if (
      ds.includes("download") ||
      ds.includes("firmware server") ||
      ds.includes("updating firmware") ||
      ds.includes("ota update") ||
      ds.includes("starting ota")
    ) {
      dispatch(
        startUpdate({ sn: serialNumber, firmware: device?.firmware_version })
      );
      timerRef.current = setTimeout(() => {
        dispatch(clearUpdate(serialNumber));
      }, TIMEOUT_MS);
    }
  }, [
    device?.device_status,
    phase,
    serialNumber,
    device?.firmware_version,
    dispatch,
  ]);

  // Phase transitions based on device_status changes
  useEffect(() => {
    if (!phase || phase === "complete") return;

    const ds = device?.device_status?.toLowerCase() || "";

    if (
      ds.includes("download") ||
      ds.includes("firmware server") ||
      ds.includes("firmware download requested")
    ) {
      if (phase !== "downloading") {
        dispatch(setUpdatePhase({ sn: serialNumber, phase: "downloading" }));
      }
    } else if (
      ds.includes("updating firmware") ||
      ds.includes("ota update") ||
      ds.includes("starting ota")
    ) {
      dispatch(setUpdatePhase({ sn: serialNumber, phase: "installing" }));
    } else if (ds.includes("rebooting")) {
      dispatch(setUpdatePhase({ sn: serialNumber, phase: "rebooting" }));
    } else if (ds.includes("powered on") || ds.includes("reconnected")) {
      // Device rebooted after update — update is complete
      dispatch(setUpdatePhase({ sn: serialNumber, phase: "complete" }));
      clearTimeout(timerRef.current);
    }
  }, [
    device?.device_status,
    device?.firmware_version,
    phase,
    serialNumber,
    dispatch,
  ]);

  // Auto-clear "complete" phase after 5 seconds
  useEffect(() => {
    if (phase === "complete") {
      const t = setTimeout(() => dispatch(clearUpdate(serialNumber)), 5000);
      return () => clearTimeout(t);
    }
  }, [phase, serialNumber, dispatch]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  const triggerUpdate = useCallback(async () => {
    dispatch(
      startUpdate({ sn: serialNumber, firmware: device?.firmware_version })
    );
    await updateDevice({ sn: serialNumber }).unwrap();
    timerRef.current = setTimeout(() => {
      dispatch(clearUpdate(serialNumber));
    }, TIMEOUT_MS);
  }, [serialNumber, device?.firmware_version, updateDevice, dispatch]);

  return { phase, isUpdating, triggerUpdate };
};

export default useFirmwareUpdate;
